import { useState, useRef, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useAuth } from '../../auth/AuthContext';
import { LogOut, ChevronDown, Menu, X, Lock as LockIcon, Headset } from 'lucide-react';
import { notifications } from '../../api/index';
import DOMPurify from 'dompurify';
import { ACCOUNT_SECTIONS, allNavItems, isItemActive, resolveSections } from '../../config/navigation';
import SetupWizardModal, { hasShippingOrigin } from '../SetupWizardModal';
import { getCompanyProfileShared, refreshCompanyProfile } from '../../api/companyProfile';
import { useSeo } from '../../utils/seo';
import { HeaderSlotContext } from './headerSlot';
import HelpDrawer from './HelpDrawer';

const Layout = () => {
  // Every signed-in route renders inside this shell, so one call keeps the
  // whole console out of search results and off the generic entry title.
  useSeo({ title: 'Console' });

  const { user, logout, hasFeature, hasMenuPermission, can, updateUser } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Stable, so the drawer's open effect doesn't re-run on every render.
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Where the page draws its title (see PageHeader). State, not a ref, so the
  // page re-renders into it once the bar has mounted.
  const [titleSlot, setTitleSlot] = useState<HTMLElement | null>(null);

  // Shown only while the page has not drawn a title of its own: the menu name
  // of the page you are on, most specific path first.
  const fallbackTitle =
    allNavItems()
      .filter((item) => isItemActive(item, pathname))
      .sort((a, b) => b.path.length - a.path.length)[0]?.label ?? '';

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  /**
   * Setup state, checked on every load rather than only at login.
   *
   * The login flag fires once and is gone; without a second way in, an admin who
   * closed the wizard would be left with locked screens and nothing saying why.
   * The profile call already returns the tenant's settings, so this needs no new
   * endpoint.
   *
   * Company admins only: nobody else can save an origin, so nobody else should
   * be told to.
   */
  const [needsSetup, setNeedsSetup] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const refreshSetupState = () => {
    if (user?.role !== 'company_admin') return;
    getCompanyProfileShared()
      .then((p: any) => setNeedsSetup(!hasShippingOrigin(p?.defaultShippingOrigin)))
      // Silent: a prompt is an enhancement, and a failed lookup must not put a
      // "finish setup" banner in front of a company that is already set up.
      .catch(() => setNeedsSetup(false));
  };

  useEffect(refreshSetupState, [user?.role]);

  // The server opens it on a first login; the banner opens it any time after.
  useEffect(() => {
    if (user?.showSetupWizard) setWizardOpen(true);
  }, [user?.showSetupWizard]);

  const [activeBanners, setActiveBanners] = useState<any[]>([]);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const banners = await notifications.getActive();
        setActiveBanners(banners);
      } catch (err) {
        console.error('Failed to fetch system banners', err);
      }
    };
    if (user && user.role !== 'super_admin') {
      fetchBanners();
    }
    // Keyed on the role, not the whole `user` object. The sidebar calls
    // updateUser() after loading the company profile, which gives `user` a new
    // identity and re-ran this — so every page load fetched the banners twice.
  }, [user?.role]);

  const handleDismiss = async (id: string) => {
    try {
      await notifications.dismiss(id);
      setActiveBanners((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      console.error('Failed to dismiss notification', err);
      setActiveBanners((prev) => prev.filter((b) => b.id !== id));
    }
  };


  const accountSections = resolveSections(ACCOUNT_SECTIONS, {
    can,
    hasFeature,
    hasMenuPermission,
    planExempt: !!user?.noPlanNeed,
  });

  return (
    <div
      /*
        `overflow-x-clip`, NOT `overflow-x-hidden`.

        When one axis is `hidden`, CSS computes the other axis to `auto` — so
        `overflow-x-hidden` quietly turned this wrapper into a scroll container.
        Every `position: sticky` descendant then measured itself against THIS
        element's scrollport rather than the viewport, and since the wrapper is
        `min-h-screen` it grows with its content and never scrolls — so nothing
        inside it could ever stick. The page scrolls on the body; the sticky
        elements were waiting on a scroll that happens somewhere else.

        `clip` suppresses horizontal overflow exactly the same way WITHOUT
        creating a scroll container, which is what makes sticky work again.
      */
      className="min-h-screen bg-slate-100 text-slate-900 overflow-x-clip print:bg-white"
    >
      <Sidebar isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      <main className="flex min-h-screen min-w-0 w-full flex-col lg:ml-[88px] lg:w-[calc(100%-88px)]">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center gap-4 sticky top-0 z-30 shadow-sm print:hidden">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0047AB]/30 lg:hidden"
              aria-label="Open navigation menu"
              aria-controls="primary-navigation"
              aria-expanded={mobileMenuOpen}
            >
              <Menu size={22} />
            </button>
            {/* The page title (PageHeader). Until a page draws one, the menu
                name of the page stands in for it. */}
            <div ref={setTitleSlot} className="peer min-w-0 flex-1 empty:hidden" />
            <p className="hidden min-w-0 flex-1 truncate text-lg font-semibold text-slate-900 peer-empty:block sm:text-xl">
              {fallbackTitle}
            </p>
          </div>
          
          <div className="flex shrink-0 items-center gap-2 sm:gap-4">
            {/* Top Menu Support Link (Hidden for Super Admin) */}
            {can('shipping.view') && (
              <button
                type="button"
                onClick={() => setHelpOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-700 hover:text-[#0047AB] hover:bg-[#E6EDF7]/70 border border-slate-200/80 rounded-xl transition-all shadow-xs"
                title="Help articles and contact support"
                aria-haspopup="dialog"
                aria-expanded={helpOpen}
              >
                <Headset size={17} className="text-[#0047AB] shrink-0" />
                <span className="hidden sm:inline">Support</span>
              </button>
            )}

            {/* User Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                id="header-user-menu"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 sm:gap-3 hover:bg-slate-50 rounded-xl px-2 sm:px-3 py-2 transition-colors"
                aria-label="Open account menu"
                aria-expanded={dropdownOpen}
              >
                <div className="text-right hidden md:block">
                  <div className="text-sm font-semibold text-slate-900">{user?.name || 'User'}</div>
                  <div className="text-xs text-slate-500">{user?.email}</div>
                </div>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#336CBC] to-[#0047AB] flex items-center justify-center text-white font-bold text-sm shadow-md shadow-[#0047AB]/30">
                  {(user?.name || 'U').charAt(0).toUpperCase()}
                </div>
                <ChevronDown
                  size={14}
                  className={`text-slate-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-60 max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl shadow-slate-200/80 py-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                  {/* User info mini header */}
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-900 truncate">{user?.name || 'User'}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                    {user?.role === 'super_admin' ? (
                      <p className="mt-1 text-xs text-slate-500 truncate">Platform administrator</p>
                    ) : (
                      <p className="mt-1 text-xs text-slate-500 truncate">
                        {user?.companyName || 'Company'}
                        {user?.activePlanName ? ` · ${user.activePlanName}` : ''}
                      </p>
                    )}
                  </div>

                  {accountSections.map((section) => (
                    <div key={section.id} className="border-b border-slate-100 last:border-b-0 py-1">
                      {section.label && (
                        <p className="px-4 pt-1.5 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                          {section.label}
                        </p>
                      )}
                      {section.items.map(({ item, visibility }) => {
                        const Icon = item.icon;
                        const locked = visibility === 'locked';
                        return (
                          <Link
                            key={item.id}
                            to={locked ? '/settings/subscription' : item.path}
                            id={`menu-${item.id}`}
                            title={locked ? `${item.label} is not included in your current plan` : undefined}
                            onClick={() => setDropdownOpen(false)}
                            className={`flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors ${
                              locked
                                ? 'text-slate-500 hover:bg-slate-50'
                                : 'text-slate-700 hover:bg-[#E6EDF7] hover:text-[#003585]'
                            }`}
                          >
                            <Icon size={15} className="text-slate-500" />
                            <span className="truncate">{item.label}</span>
                            {locked && <LockIcon size={12} className="ml-auto shrink-0" />}
                          </Link>
                        );
                      })}
                    </div>
                  ))}

                  <div className="border-t border-slate-100 py-1">
                    <button
                      id="menu-logout"
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut size={15} />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Active Banners */}
        {activeBanners.length > 0 && (
          <div className="px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8 space-y-3 print:hidden">
            {/*
              Setup is outstanding. Deliberately NOT styled like the system
              banners above: those are announcements, this is an unfinished job
              with an action attached, and it is the only route back into the
              wizard once the first-login prompt is gone.
            */}
            {needsSetup && !wizardOpen && (
              <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3.5 sm:px-5 flex items-center gap-3 shadow-sm">
                <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                  <LockIcon size={15} className="text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">Finish setting up to quote rates</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Rates are measured from your ship-from address, and there isn't one saved yet.
                  </div>
                </div>
                <button
                  onClick={() => setWizardOpen(true)}
                  className="shrink-0 bg-slate-900 text-white rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-slate-800 transition-colors"
                >
                  Finish setup
                </button>
              </div>
            )}

            {activeBanners.map((banner) => (
              <div
                key={banner.id}
                className="relative bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 text-white rounded-2xl p-4 sm:p-5 shadow-lg shadow-amber-500/10 flex items-start gap-3 sm:gap-4 transition-all duration-300 animate-in fade-in slide-in-from-top-4 border border-amber-400"
              >
                <div className="flex-1 space-y-1">
                  <h4 className="font-bold text-sm">{banner.title}</h4>
                  <div
                    className="text-white text-sm font-medium prose prose-invert max-w-none break-words"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(banner.message) }}
                  />
                </div>
                <button
                  onClick={() => handleDismiss(banner.id)}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-all self-start"
                  title="Dismiss Banner"
                >
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8 print:p-0">
          <HeaderSlotContext.Provider value={titleSlot}>
            <Outlet />
          </HeaderSlotContext.Provider>

          <HelpDrawer open={helpOpen} onClose={closeHelp} />

          {/*
            First-run setup. The SERVER decides whether this opens — it is true
            only on a company admin's first sign-in at a company with no origin
            address — so there is no client-side guess about who is new.

            Closing clears the stored flag, so a refresh in the same session does
            not reopen a wizard the admin has already dealt with.
          */}
          <SetupWizardModal
            open={wizardOpen}
            onClose={() => {
              setWizardOpen(false);
              // Clears the one-shot flag so a refresh in this session does not
              // reopen a wizard the admin has already dealt with. The banner is
              // what brings them back if setup is still outstanding.
              updateUser({ showSetupWizard: false });
              // Forced, not shared: the wizard has just changed the very
              // thing this reads, so the cached copy is known stale.
              refreshCompanyProfile()
                .then((p: any) =>
                  setNeedsSetup(!hasShippingOrigin(p?.defaultShippingOrigin)),
                )
                .catch(() => setNeedsSetup(false));
            }}
            onAddressSaved={() => setNeedsSetup(false)}
          />
        </div>

        {/* Footer */}
        <footer className="border-t border-slate-200/60 py-6 px-4 sm:px-6 lg:px-8 text-center text-slate-500 text-xs sm:text-sm print:hidden">
          <div>
            &copy; {new Date().getFullYear()} Nixook.com. All rights reserved.
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Layout;
